import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorAPI } from '../services/api';
import Loading from '../components/common/Loading';

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await vendorAPI.getAll();
      if (response.data.success) {
        setVendors(response.data.data.data || []);
      }
    } catch (err) {
      setError('Failed to fetch vendors');
      console.error('Error fetching vendors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewProducts = (vendorId) => {
    // Navigate to products page with vendor filter
    navigate(`/products?vendor_id=${vendorId}`);
  };

  const handleContact = (vendor) => {
    // Create email link with pre-filled subject
    const subject = encodeURIComponent(`Inquiry from Infini BuildComm - ${vendor.vendor_name}`);
    const body = encodeURIComponent(`Hello ${vendor.contact_person},\n\nI am interested in your products listed on Infini BuildComm. Please provide more information.\n\nThank you!`);
    const emailLink = `mailto:${vendor.email}?subject=${subject}&body=${body}`;
    
    // Open email client
    window.location.href = emailLink;
  };

  if (loading) return <Loading message="Loading vendors..." />;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Our Trusted Vendors</h1>
          <p className="text-gray-600 text-lg">
            Partner with verified suppliers for all your construction material needs.
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {vendors.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No vendors found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vendors.map((vendor) => (
              <div key={vendor.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {vendor.vendor_name.charAt(0)}
                      </span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-gray-900">
                        {vendor.vendor_name}
                      </h3>
                      <p className="text-gray-600">
                        {vendor.products?.length || 0} Products
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Contact Person</p>
                      <p className="text-gray-900">{vendor.contact_person}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700">Phone</p>
                      <p className="text-gray-900">{vendor.phone}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700">Email</p>
                      <p className="text-gray-900">{vendor.email}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700">Address</p>
                      <p className="text-gray-900">{vendor.address}</p>
                    </div>
                  </div>

                  {vendor.products && vendor.products.length > 0 && (
                    <div className="mt-6 border-t pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Sample Products:
                      </p>
                      <div className="space-y-1">
                        {vendor.products.slice(0, 3).map((product) => (
                          <p key={product.id} className="text-sm text-gray-600">
                            • {product.product_name}
                          </p>
                        ))}
                        {vendor.products.length > 3 && (
                          <p className="text-sm text-blue-600 font-medium">
                            +{vendor.products.length - 3} more products
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex space-x-3">
                    <button 
                      onClick={() => handleViewProducts(vendor.id)}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      View Products
                    </button>
                    <button 
                      onClick={() => handleContact(vendor)}
                      className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
                    >
                      Contact
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Vendors;
